import { create } from 'zustand';
import { getCurrentDate } from '../services/billService';

const EMPTY_BILL_FORM = {
  date: getCurrentDate(),
  customerId: '',
  employeeId: '',
  services: [],
  price: '',
  discount: '',
  paymentType: 'Nakit',
  status: 'active',
  note: '',
  selectedCustomer: null,
  selectedEmployee: null,
  trackingInfo: {},
  loyaltyPointsUsed: 0,
  loyaltyDiscount: 0,
  packageDiscount: 0,
};

const EMPTY_COST_FORM = {
  date: getCurrentDate(),
  description: '',
  price: '',
  paymentType: 'Nakit',
  categoryId: '',
  categoryName: '',
  subCategory: '',
};

/**
 * bills feature UI state.
 * Firestore verisi (bills, customers, employees, services) burada YAŞAMAZ
 * — yalnızca modal açık/kapalı, form taslağı, filtreler.
 */
const useBillsStore = create((set) => ({
  // ── Tarih ──────────────────────────────────────────────────
  today: getCurrentDate(),
  setToday: (date) => set({ today: date }),

  // ── Arama ──────────────────────────────────────────────────
  searchTerm: '',
  setSearchTerm: (term) => set({ searchTerm: term }),

  // ── Bill Modal ─────────────────────────────────────────────
  modalIsOpen: false,
  isEditing: false,
  selectedBillId: null,
  originalBillCustomerId: null,
  billForm: EMPTY_BILL_FORM,
  applyDiscount: false,
  discountAmount: 0,
  selectedServices: [],
  useLoyaltyPoints: false,
  pointsToUse: 0,
  isSaving: false,

  // Paket state
  availablePackages: [],
  customerActivePackages: [],
  servicePackageUsage: {},

  setBillForm: (formOrUpdater) =>
    set((state) => ({
      billForm:
        typeof formOrUpdater === 'function'
          ? formOrUpdater(state.billForm)
          : formOrUpdater,
    })),
  setApplyDiscount: (v) => set({ applyDiscount: v }),
  setDiscountAmount: (v) => set({ discountAmount: v }),
  setSelectedServices: (v) => set({ selectedServices: v }),
  setUseLoyaltyPoints: (v) => set({ useLoyaltyPoints: v }),
  setPointsToUse: (v) => set({ pointsToUse: v }),
  setIsSaving: (v) => set({ isSaving: v }),
  setAvailablePackages: (v) => set({ availablePackages: v }),
  setCustomerActivePackages: (v) => set({ customerActivePackages: v }),
  setServicePackageUsage: (updater) =>
    set((state) => ({
      servicePackageUsage:
        typeof updater === 'function' ? updater(state.servicePackageUsage) : updater,
    })),

  openBillModal: (bill = null) =>
    set(
      bill
        ? {
            modalIsOpen: true,
            isEditing: true,
            selectedBillId: bill.id,
          }
        : {
            modalIsOpen: true,
            isEditing: false,
            selectedBillId: null,
            originalBillCustomerId: null,
            billForm: { ...EMPTY_BILL_FORM, date: getCurrentDate() },
            applyDiscount: false,
            discountAmount: 0,
            selectedServices: [],
            useLoyaltyPoints: false,
            pointsToUse: 0,
            servicePackageUsage: {},
            customerActivePackages: [],
          }
    ),

  closeBillModal: () =>
    set({
      modalIsOpen: false,
      // Formu ve servisleri sıfırla — aksi halde bir sonraki açılışta (özellikle
      // randevudan adisyon oluştururken) önceki adisyonun servisleri / trackingInfo'su
      // store'da kalıp sızabiliyor.
      billForm: { ...EMPTY_BILL_FORM, date: getCurrentDate() },
      selectedServices: [],
      applyDiscount: false,
      discountAmount: 0,
      useLoyaltyPoints: false,
      pointsToUse: 0,
      servicePackageUsage: {},
      customerActivePackages: [],
    }),

  setEditingBillData: (payload) => set(payload),

  // ── Cost Modal ─────────────────────────────────────────────
  costModalIsOpen: false,
  selectedCostId: null,
  costForm: EMPTY_COST_FORM,
  isCostSaving: false,

  setCostForm: (formOrUpdater) =>
    set((state) => ({
      costForm:
        typeof formOrUpdater === 'function'
          ? formOrUpdater(state.costForm)
          : formOrUpdater,
    })),
  setIsCostSaving: (v) => set({ isCostSaving: v }),

  openCostModal: (cost = null) =>
    set(
      cost
        ? {
            costModalIsOpen: true,
            selectedCostId: cost.id,
            costForm: {
              date: cost.date,
              description: cost.description,
              price: cost.price,
              paymentType: cost.paymentType || 'Nakit',
              categoryId: cost.categoryId || '',
              categoryName: cost.categoryName || '',
              subCategory: cost.subCategory || '',
            },
          }
        : {
            costModalIsOpen: true,
            selectedCostId: null,
            costForm: { ...EMPTY_COST_FORM, date: getCurrentDate() },
          }
    ),

  closeCostModal: () => set({ costModalIsOpen: false }),
}));

export default useBillsStore;
